/**
 * Procurement Dashboard — Phase P3-8.
 *
 * Executive, read-only dashboard that maps POs, RFQs, GRNs and Invoices
 * into a single-screen Hybrid Elite grid. Zero infinite scroll, sticky
 * filter bar, drill-down navigation, multi-currency aware.
 *
 * Route: /admin/procurement/dashboard
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  LayoutDashboard,
  ShoppingCart,
  Clock,
  Truck,
  CheckCircle2,
  FileSignature,
  Package,
  Receipt,
  Star,
  RefreshCw,
  AlertTriangle,
  TrendingUp,
} from 'lucide-react';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';

import { Card, CardContent } from '../../../../components/ui/card';
import { Button } from '../../../../components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../../components/ui/select';
import BackButton from '../../../../components/admin/BackButton';
import AIProcurementBrief from './AIProcurementBrief';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Hybrid Elite palette
const THEME = {
  primary: '#023E8A',
  accent: '#FF6B35',
  teal: '#0F766E',
  amber: '#D97706',
  danger: '#DC2626',
  success: '#16A34A',
  slate: '#475569',
};

const CATEGORY_COLORS = [
  THEME.primary, THEME.accent, THEME.teal, THEME.amber,
  THEME.success, THEME.danger, THEME.slate, '#7C3AED', '#0891B2', '#B45309',
];

const GRN_STATUS_COLORS = {
  draft: THEME.slate,
  partial: THEME.amber,
  received: THEME.success,
  rejected: THEME.danger,
};

const DATE_PRESETS = {
  this_month: 'This month',
  qtd: 'Quarter to date',
  ytd: 'Year to date',
  last_12m: 'Last 12 months',
  all: 'All time',
};

// ------ helpers ------

const fmtMoney = (v, ccy) => {
  const num = Number(v || 0);
  try {
    return new Intl.NumberFormat('en-AE', {
      style: 'currency', currency: ccy || 'AED', maximumFractionDigits: 0,
    }).format(num);
  } catch {
    return `${ccy || 'AED'} ${num.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
  }
};

const fmtCount = (v) => Number(v || 0).toLocaleString('en-US');

const computeDateRange = (preset) => {
  const now = new Date();
  const y = now.getUTCFullYear();
  const m = now.getUTCMonth();
  let from = null;
  let to = null;
  if (preset === 'this_month') {
    from = new Date(Date.UTC(y, m, 1));
  } else if (preset === 'qtd') {
    const qStartMonth = Math.floor(m / 3) * 3;
    from = new Date(Date.UTC(y, qStartMonth, 1));
  } else if (preset === 'ytd') {
    from = new Date(Date.UTC(y, 0, 1));
  } else if (preset === 'last_12m') {
    from = new Date(Date.UTC(y, m - 11, 1));
  }
  if (from) {
    to = new Date(Date.UTC(y, m + 1, 0, 23, 59, 59));
  }
  return { from, to };
};

// ------ Sub-components ------

const KPITile = ({ label, value, sublabel, icon: Icon, color, onClick, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className="group relative overflow-hidden rounded-lg bg-white border border-slate-200 hover:border-slate-300 px-4 py-3 text-left transition-all hover:shadow-md"
    style={{ borderLeftColor: color, borderLeftWidth: 3 }}
  >
    <div className="flex items-start justify-between">
      <div className="flex-1 min-w-0">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
          {label}
        </p>
        <p className="mt-1 text-2xl font-bold text-slate-900 truncate" style={{ color }}>
          {value}
        </p>
        {sublabel && (
          <p className="mt-0.5 text-[11px] text-slate-500 truncate" title={sublabel}>
            {sublabel}
          </p>
        )}
      </div>
      <div
        className="ml-2 flex h-8 w-8 shrink-0 items-center justify-center rounded-md"
        style={{ backgroundColor: `${color}15`, color }}
      >
        <Icon className="h-4 w-4" />
      </div>
    </div>
  </button>
);

const ChartCard = ({ title, children, testId, right }) => (
  <Card className="h-full" data-testid={testId}>
    <CardContent className="p-4 h-full flex flex-col">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-slate-800">{title}</h3>
        {right}
      </div>
      <div className="flex-1 min-h-0">{children}</div>
    </CardContent>
  </Card>
);

const EmptyChart = ({ message = 'No data for the selected range' }) => (
  <div className="flex h-full min-h-[160px] flex-col items-center justify-center text-slate-400">
    <TrendingUp className="h-8 w-8 mb-2 opacity-40" />
    <p className="text-xs">{message}</p>
  </div>
);

// ------ Page ------

const ProcurementDashboard = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('admin_token');
  const auth = useMemo(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token],
  );

  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState('');
  const [datePreset, setDatePreset] = useState('ytd');
  const [currency, setCurrency] = useState('');

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const [kpis, setKpis] = useState(null);
  const [trend, setTrend] = useState([]);
  const [bySupplier, setBySupplier] = useState([]);
  const [byCategory, setByCategory] = useState([]);
  const [delivery, setDelivery] = useState(null);
  const [grnStatus, setGrnStatus] = useState(null);

  const dateRange = useMemo(() => computeDateRange(datePreset), [datePreset]);

  const buildScopeParams = useCallback(() => {
    const p = new URLSearchParams();
    if (companyId) p.append('company_id', companyId);
    if (currency) p.append('currency', currency);
    if (dateRange.from) p.append('date_from', dateRange.from.toISOString());
    if (dateRange.to) p.append('date_to', dateRange.to.toISOString());
    return p;
  }, [companyId, currency, dateRange]);

  const fetchAll = useCallback(async () => {
    try {
      setRefreshing(true);
      setError(null);
      const base = `${API_URL}/api/procurement/dashboard`;
      const scope = buildScopeParams().toString();
      const trendParams = new URLSearchParams();
      trendParams.append('months', '12');
      if (companyId) trendParams.append('company_id', companyId);
      if (currency) trendParams.append('currency', currency);
      const [k, t, s, c, d, g] = await Promise.all([
        axios.get(`${base}/kpis?${scope}`, auth),
        axios.get(`${base}/spend-trend?${trendParams.toString()}`, auth),
        axios.get(`${base}/spend-by-supplier?limit=10&${scope}`, auth),
        axios.get(`${base}/spend-by-category?limit=10&${scope}`, auth),
        axios.get(`${base}/delivery-performance?${scope}`, auth),
        axios.get(`${base}/grn-status-breakdown?${scope}`, auth),
      ]);
      setKpis(k.data);
      setTrend(t.data || []);
      setBySupplier(s.data || []);
      setByCategory(c.data || []);
      setDelivery(d.data);
      setGrnStatus(g.data);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || 'Failed to load dashboard');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [auth, buildScopeParams, companyId, currency]);

  // Initial load of companies + dashboard
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API_URL}/api/procurement/companies`, auth);
        if (!cancelled) setCompanies(r.data || []);
      } catch {
        /* non-blocking */
      }
    })();
    return () => { cancelled = true; };
  }, [auth]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // Prepare data derivations
  const mainCurrency = useMemo(() => {
    if (currency) return currency;
    const first = kpis?.open_pos?.by_currency?.[0]?.currency
      || kpis?.invoices_to_pay?.by_currency?.[0]?.currency
      || 'AED';
    return first;
  }, [currency, kpis]);

  const openPoSummary = useMemo(() => {
    const list = kpis?.open_pos?.by_currency || [];
    if (!list.length) return { value: 0, sublabel: '—' };
    const primary = list.find((r) => r.currency === mainCurrency) || list[0];
    const other = list.filter((r) => r.currency !== primary.currency);
    return {
      value: fmtMoney(primary.total, primary.currency),
      sublabel: other.length
        ? `+ ${other.map((r) => fmtMoney(r.total, r.currency)).join(' · ')}`
        : `${fmtCount(kpis?.open_pos?.count || 0)} purchase orders`,
    };
  }, [kpis, mainCurrency]);

  const invoicesSummary = useMemo(() => {
    const list = kpis?.invoices_to_pay?.by_currency || [];
    if (!list.length) return { value: fmtMoney(0, mainCurrency), sublabel: 'Nothing outstanding' };
    const primary = list.find((r) => r.currency === mainCurrency) || list[0];
    const other = list.filter((r) => r.currency !== primary.currency);
    return {
      value: fmtMoney(primary.total, primary.currency),
      sublabel: other.length
        ? `+ ${other.map((r) => fmtMoney(r.total, r.currency)).join(' · ')}`
        : `${fmtCount(kpis?.invoices_to_pay?.count || 0)} invoices outstanding`,
    };
  }, [kpis, mainCurrency]);

  // Recharts-friendly trend data: one row per month, currencies as keys
  const trendChartData = useMemo(() => {
    const map = new Map();
    const currencies = new Set();
    (trend || []).forEach((r) => {
      if (!map.has(r.month)) map.set(r.month, { month: r.month });
      const entry = map.get(r.month);
      entry[r.currency] = r.total;
      currencies.add(r.currency);
    });
    return { rows: Array.from(map.values()), currencies: Array.from(currencies) };
  }, [trend]);

  const grnPieData = useMemo(() => {
    if (!grnStatus) return [];
    return ['draft', 'partial', 'received', 'rejected']
      .map((s) => ({ name: s, value: grnStatus[s] || 0, fill: GRN_STATUS_COLORS[s] }))
      .filter((r) => r.value > 0);
  }, [grnStatus]);

  const deliveryBucketsData = useMemo(() => (
    (delivery?.buckets || []).map((b) => ({
      range: b.range,
      count: b.count,
    }))
  ), [delivery]);

  // ------ UI ------

  if (loading) {
    return (
      <div className="p-6">
        <BackButton />
        <div className="mt-6 text-center text-slate-500">Loading dashboard…</div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-4" data-testid="procurement-dashboard-page">
      <BackButton />

      {/* Header + filters (sticky) */}
      <div
        className="sticky top-0 z-10 -mx-6 px-6 py-3 backdrop-blur-md bg-white/85 border-b border-slate-200"
        style={{ boxShadow: '0 1px 2px rgba(15,23,42,0.04)' }}
        data-testid="dashboard-filter-bar"
      >
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div
              className="flex h-9 w-9 items-center justify-center rounded-md"
              style={{ backgroundColor: `${THEME.primary}12`, color: THEME.primary }}
            >
              <LayoutDashboard className="h-5 w-5" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-slate-900">Procurement Dashboard</h1>
              <p className="text-xs text-slate-500">
                Executive view — POs, GRNs, Invoices, Suppliers
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Select value={companyId || 'all'} onValueChange={(v) => setCompanyId(v === 'all' ? '' : v)}>
              <SelectTrigger className="h-9 w-[200px]" data-testid="filter-company">
                <SelectValue placeholder="All companies" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All companies</SelectItem>
                {companies.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.trading_name || c.legal_name || 'Company'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={datePreset} onValueChange={setDatePreset}>
              <SelectTrigger className="h-9 w-[170px]" data-testid="filter-date">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(DATE_PRESETS).map(([k, label]) => (
                  <SelectItem key={k} value={k}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={currency || 'all'} onValueChange={(v) => setCurrency(v === 'all' ? '' : v)}>
              <SelectTrigger className="h-9 w-[130px]" data-testid="filter-currency">
                <SelectValue placeholder="All currencies" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All currencies</SelectItem>
                <SelectItem value="AED">AED</SelectItem>
                <SelectItem value="USD">USD</SelectItem>
                <SelectItem value="EUR">EUR</SelectItem>
                <SelectItem value="GBP">GBP</SelectItem>
              </SelectContent>
            </Select>

            <Button
              onClick={fetchAll}
              disabled={refreshing}
              variant="outline"
              className="h-9"
              data-testid="dashboard-refresh"
            >
              <RefreshCw className={`h-4 w-4 mr-1 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>
      </div>

      {error && (
        <div
          className="flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
          data-testid="dashboard-error"
        >
          <AlertTriangle className="h-4 w-4" />
          <span>{error}</span>
        </div>
      )}

      {/* Row 1 — KPI tiles (8 tiles in 2 rows of 4) */}
      <AIProcurementBrief companyId={companyId} currency={currency} />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPITile
          label="Open POs"
          value={openPoSummary.value}
          sublabel={openPoSummary.sublabel}
          icon={ShoppingCart}
          color={THEME.primary}
          onClick={() => navigate('/admin/procurement/purchase-orders')}
          testId="kpi-open-pos"
        />
        <KPITile
          label="Pending approval"
          value={fmtCount(kpis?.pending_approval || 0)}
          sublabel="Awaiting sign-off"
          icon={Clock}
          color={THEME.amber}
          onClick={() => navigate('/admin/procurement/purchase-orders')}
          testId="kpi-pending-approval"
        />
        <KPITile
          label="Partially delivered"
          value={fmtCount(kpis?.partially_delivered || 0)}
          sublabel="POs in transit"
          icon={Truck}
          color={THEME.accent}
          onClick={() => navigate('/admin/procurement/purchase-orders')}
          testId="kpi-partial"
        />
        <KPITile
          label="Fully delivered"
          value={fmtCount(kpis?.fully_delivered?.this_month || 0)}
          sublabel={`YTD: ${fmtCount(kpis?.fully_delivered?.ytd || 0)}`}
          icon={CheckCircle2}
          color={THEME.success}
          onClick={() => navigate('/admin/procurement/purchase-orders')}
          testId="kpi-fully-delivered"
        />

        <KPITile
          label="RFQs open"
          value={fmtCount(kpis?.rfqs_open || 0)}
          sublabel="Draft · Sent · Received"
          icon={FileSignature}
          color={THEME.teal}
          onClick={() => navigate('/admin/procurement/rfqs')}
          testId="kpi-rfqs-open"
        />
        <KPITile
          label="GRNs this month"
          value={fmtCount(kpis?.grns_this_month?.total || 0)}
          sublabel={`Draft ${kpis?.grns_this_month?.by_status?.draft || 0} · Partial ${kpis?.grns_this_month?.by_status?.partial || 0} · Received ${kpis?.grns_this_month?.by_status?.received || 0}`}
          icon={Package}
          color="#7C3AED"
          onClick={() => navigate('/admin/procurement/grns')}
          testId="kpi-grns"
        />
        <KPITile
          label="Invoices to pay"
          value={invoicesSummary.value}
          sublabel={invoicesSummary.sublabel}
          icon={Receipt}
          color={THEME.danger}
          onClick={() => navigate('/admin/procurement/invoices')}
          testId="kpi-invoices-to-pay"
        />
        <KPITile
          label="Top supplier (MTD)"
          value={kpis?.top_supplier
            ? fmtMoney(kpis.top_supplier.total, kpis.top_supplier.currency)
            : '—'}
          sublabel={kpis?.top_supplier
            ? `${kpis.top_supplier.name} · ${kpis.top_supplier.po_count} PO(s)`
            : 'No activity this month'}
          icon={Star}
          color="#B45309"
          onClick={() => navigate('/admin/procurement/suppliers/ranking')}
          testId="kpi-top-supplier"
        />
      </div>

      {/* Row 2 — Spend trend (full width) */}
      <div className="grid grid-cols-1">
        <ChartCard
          title="PO Spend Trend — Last 12 months"
          testId="chart-spend-trend"
          right={
            <span className="text-xs text-slate-500">
              {currency ? currency : 'All currencies'} · POs excluding Draft
            </span>
          }
        >
          {trendChartData.rows.length === 0 || trendChartData.currencies.length === 0 ? (
            <EmptyChart />
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={trendChartData.rows} margin={{ top: 5, right: 15, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#64748B' }} />
                <YAxis tick={{ fontSize: 11, fill: '#64748B' }} tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}K` : v)} />
                <Tooltip
                  contentStyle={{ fontSize: 12, borderRadius: 6, border: '1px solid #CBD5E1' }}
                  formatter={(value, name) => [fmtMoney(value, name), name]}
                />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {trendChartData.currencies.map((ccy, i) => (
                  <Line
                    key={ccy}
                    type="monotone"
                    dataKey={ccy}
                    stroke={CATEGORY_COLORS[i % CATEGORY_COLORS.length]}
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    activeDot={{ r: 5 }}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
      </div>

      {/* Row 3 — Supplier + Category charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <ChartCard
          title="Top 10 Suppliers by PO Value"
          testId="chart-spend-by-supplier"
          right={
            <button
              type="button"
              onClick={() => navigate('/admin/procurement/suppliers/ranking')}
              className="text-xs font-semibold text-[#023E8A] hover:underline"
              data-testid="chart-supplier-ranking-link"
            >
              View ranking →
            </button>
          }
        >
          {bySupplier.length === 0 ? (
            <EmptyChart />
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart
                data={bySupplier}
                layout="vertical"
                margin={{ top: 5, right: 20, left: 20, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis type="number" tick={{ fontSize: 11, fill: '#64748B' }} tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}K` : v)} />
                <YAxis
                  type="category"
                  dataKey="name"
                  tick={{ fontSize: 11, fill: '#334155' }}
                  width={130}
                />
                <Tooltip
                  contentStyle={{ fontSize: 12, borderRadius: 6, border: '1px solid #CBD5E1' }}
                  formatter={(value, _name, p) => [fmtMoney(value, p?.payload?.currency), 'Total']}
                />
                <Bar dataKey="total" fill={THEME.primary} radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        <ChartCard title="Spend by Category" testId="chart-spend-by-category">
          {byCategory.length === 0 ? (
            <EmptyChart />
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie
                  data={byCategory}
                  dataKey="total"
                  nameKey="category"
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={85}
                  paddingAngle={2}
                  label={({ category, percent }) => `${category} ${(percent * 100).toFixed(0)}%`}
                  labelLine={false}
                >
                  {byCategory.map((_, i) => (
                    <Cell key={i} fill={CATEGORY_COLORS[i % CATEGORY_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{ fontSize: 12, borderRadius: 6, border: '1px solid #CBD5E1' }}
                  formatter={(value, _name, p) => [fmtMoney(value, p?.payload?.currency), p?.payload?.category]}
                />
              </PieChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
      </div>

      {/* Row 4 — Delivery performance + GRN status */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <ChartCard
          title="Delivery Performance (distribution)"
          testId="chart-delivery-performance"
          right={
            <span className="text-xs text-slate-500">
              Accuracy {delivery?.delivery_accuracy ?? 0}%
              {delivery?.on_time_delivery != null ? ` · On-time ${delivery.on_time_delivery}%` : ''}
            </span>
          }
        >
          {deliveryBucketsData.every((b) => b.count === 0) ? (
            <EmptyChart />
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={deliveryBucketsData} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="range" tick={{ fontSize: 11, fill: '#64748B' }} />
                <YAxis tick={{ fontSize: 11, fill: '#64748B' }} allowDecimals={false} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6, border: '1px solid #CBD5E1' }} />
                <Bar dataKey="count" fill={THEME.accent} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        <ChartCard title="GRN Status Breakdown" testId="chart-grn-status">
          {grnPieData.length === 0 ? (
            <EmptyChart />
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie
                  data={grnPieData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius={40}
                  outerRadius={75}
                  paddingAngle={2}
                  label={({ name, value }) => `${name} ${value}`}
                  labelLine={false}
                >
                  {grnPieData.map((entry, i) => (
                    <Cell key={i} fill={entry.fill} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6, border: '1px solid #CBD5E1' }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        <Card data-testid="chart-quick-stats">
          <CardContent className="p-4 space-y-3">
            <h3 className="text-sm font-semibold text-slate-800">Quick stats</h3>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">POs analysed</span>
                <span className="font-semibold text-slate-900">{fmtCount(delivery?.total_pos || 0)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Delivery accuracy</span>
                <span className="font-semibold" style={{ color: THEME.primary }}>
                  {delivery?.delivery_accuracy ?? 0}%
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">On-time delivery</span>
                <span className="font-semibold" style={{ color: THEME.success }}>
                  {delivery?.on_time_delivery != null
                    ? `${delivery.on_time_delivery}% (n=${delivery.on_time_sample_size})`
                    : '—'}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">GRNs recorded</span>
                <span className="font-semibold text-slate-900">{fmtCount(grnStatus?.total || 0)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">RFQs open</span>
                <span className="font-semibold text-slate-900">{fmtCount(kpis?.rfqs_open || 0)}</span>
              </div>
            </div>
            <p className="text-[11px] text-slate-400 pt-2 border-t border-slate-100">
              Auto-generated at {kpis?.generated_at ? new Date(kpis.generated_at).toLocaleTimeString() : '—'}
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ProcurementDashboard;
